import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "../i18n";
import { scrollToId } from "../smooth";
import { EASE } from "./Reveal";

const LangToggle = ({ className = "" }) => {
    const { lang, setLang } = useLang();
    return (
        <div className={`flex items-center gap-2 text-[11px] tracking-[0.25em] ${className}`} data-testid="lang-toggle">
            <button
                data-testid="lang-toggle-en"
                onClick={() => setLang("en")}
                className={`transition-colors duration-500 ${lang === "en" ? "text-champagne" : "text-ivory/45 hover:text-ivory/80"}`}
            >
                EN
            </button>
            <span className="h-3 w-px bg-champagne/40" />
            <button
                data-testid="lang-toggle-it"
                onClick={() => setLang("it")}
                className={`transition-colors duration-500 ${lang === "it" ? "text-champagne" : "text-ivory/45 hover:text-ivory/80"}`}
            >
                IT
            </button>
        </div>
    );
};

const Header = () => {
    const { t } = useLang();
    const [open, setOpen] = useState(false);
    const links = [
        { id: "#collection", label: t.nav.collection, testid: "nav-link-collection" },
        { id: "#story", label: t.nav.story, testid: "nav-link-story" },
        { id: "#hospitality", label: t.nav.hospitality, testid: "nav-link-hospitality" },
        { id: "#contact", label: t.nav.contact, testid: "nav-link-contact" },
    ];
    const go = (e, id) => {
        e.preventDefault();
        setOpen(false);
        scrollToId(id);
    };

    return (
        <header
            data-testid="site-header"
            className="fixed inset-x-0 top-0 z-50 border-b border-champagne/15 bg-navy-deep/95 backdrop-blur-sm"
        >
            <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:h-[76px] md:px-10">
                <a
                    href="/"
                    onClick={(e) => go(e, "#top")}
                    data-testid="header-logo"
                    className="flex items-center gap-3"
                >
                    <img src="/assets/emblem.png" alt="LARIVA emblem" className="h-9 w-auto md:h-11" />
                    <span className="leading-none">
                        <span className="block font-serif text-xl tracking-[0.28em] text-ivory md:text-2xl">
                            LARIVA
                        </span>
                        <span className="mt-1 block text-[7px] tracking-[0.42em] text-champagne md:text-[8px]">
                            ITALIAN LAKES COLLECTION
                        </span>
                    </span>
                </a>

                <nav className="hidden items-center gap-9 lg:flex" data-testid="desktop-nav">
                    {links.map((l) => (
                        <a
                            key={l.id}
                            href={l.id}
                            data-testid={l.testid}
                            onClick={(e) => go(e, l.id)}
                            className="group relative text-[11px] tracking-[0.28em] text-ivory/75 uppercase transition-colors duration-500 hover:text-ivory"
                        >
                            {l.label}
                            <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-champagne transition-all duration-500 group-hover:w-full" />
                        </a>
                    ))}
                    <LangToggle className="ml-4" />
                    <a
                        href="#request"
                        onClick={(e) => go(e, "#request")}
                        data-testid="header-request-access"
                        className="ml-6 border border-champagne/60 px-5 py-2.5 text-[11px] tracking-[0.28em] whitespace-nowrap text-champagne uppercase transition-colors duration-500 hover:bg-champagne hover:text-navy-deep"
                    >
                        {t.nav.request}
                    </a>
                </nav>

                <div className="flex items-center gap-5 lg:hidden">
                    <LangToggle />
                    <button
                        data-testid="mobile-menu-button"
                        onClick={() => setOpen(!open)}
                        aria-label="Menu"
                        className="text-ivory"
                    >
                        {open ? <X size={22} strokeWidth={1.4} /> : <Menu size={22} strokeWidth={1.4} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="mobile-menu"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.55, ease: EASE }}
                        className="overflow-hidden border-t border-champagne/10 bg-navy-deep lg:hidden"
                    >
                        <div className="flex flex-col items-center gap-7 py-10">
                            {links.map((l) => (
                                <a
                                    key={l.id}
                                    href={l.id}
                                    data-testid={`mobile-${l.testid}`}
                                    onClick={(e) => go(e, l.id)}
                                    className="font-serif text-2xl tracking-[0.18em] text-ivory/85 uppercase"
                                >
                                    {l.label}
                                </a>
                            ))}
                            <a
                                href="#request"
                                data-testid="mobile-request-access"
                                onClick={(e) => go(e, "#request")}
                                className="mt-3 border border-champagne/60 px-7 py-3 text-[11px] tracking-[0.3em] text-champagne uppercase"
                            >
                                {t.nav.request}
                            </a>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Header;
